import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";
import cn from "~/utils/cn";

// Fade-and-rise when scrolled into view (styles in tokens.css). Server HTML is always visible: after hydration only
// blocks still off screen get data-hide, and only motion-allowing visitors see the hidden state.
export default function Reveal({
  children,
  delay = 0,
  y = 24,
  amount = 0.3,
  className,
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  amount?: number;
  className?: string;
  as?: "div" | "li" | "section" | "span";
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) return; // already on screen: never hide it
    el.dataset.hide = "";
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        delete el.dataset.hide;
        io.disconnect();
      },
      { threshold: Math.min(amount, (window.innerHeight * 0.8) / Math.max(box.height, 1)) }, // a block taller than the screen still shows
    );
    io.observe(el);
    return () => io.disconnect();
  }, [amount]);
  return (
    <Tag ref={ref as never} className={cn("reveal", className)} style={{ "--reveal-y": `${y}px`, "--reveal-delay": `${delay}s` } as CSSProperties}>
      {children}
    </Tag>
  );
}
